<template>
  <div class="education-repeater">
    <div v-for="(entry, index) in modelValue || []" :key="index" class="card mb-3 education-entry">
      <div class="card-body">
        <div class="d-flex justify-content-between align-items-center mb-3">
          <h5 class="card-title mb-0">Education #{{ index + 1 }}</h5>
          <button
            v-if="(modelValue || []).length > 1"
            class="btn btn-outline-danger btn-sm"
            type="button"
            @click="removeEntry(index)"
          >
            Remove
          </button>
        </div>

        <div class="row g-3">
          <!-- School -->
          <div class="col-12 position-relative">
            <label :for="'school_' + index" class="form-label">School</label>
            <div class="input-group">
              <input
                :id="'school_' + index"
                v-model="searchTerms[index]"
                :class="{
                  'is-invalid': invalid && !entry.school_name_id
                }"
                autocomplete="off"
                class="form-control"
                placeholder="Search School..."
                type="text"
                @blur="onBlur(index)"
                @focus="showResults[index] = true"
                @input="onInput(index)"
              />
              <button
                v-if="entry.school_name_id"
                class="btn btn-outline-secondary"
                type="button"
                @click="clearSchool(index)"
              >
                &times;
              </button>
            </div>
            <input v-model="entry.school_name_id" :required="required" type="hidden" />

            <!-- Search Results Dropdown -->
            <ul
              v-if="showResults[index] && schoolResults[index]?.length > 0"
              class="dropdown-menu show w-100 shadow-sm"
              style="max-height: 200px; overflow-y: auto"
            >
              <li v-for="s in schoolResults[index]" :key="s.id">
                <a class="dropdown-item py-2" href="#" @mousedown.prevent="selectSchool(index, s)">
                  {{ s.text || s.name }}
                </a>
              </li>
            </ul>
            <ul
              v-else-if="
                showResults[index] && searchTerms[index]?.length >= 2 && !isSearching[index]
              "
              class="dropdown-menu show w-100 shadow-sm"
            >
              <li class="dropdown-item disabled py-2 text-muted">No schools found</li>
            </ul>
            <div
              v-if="isSearching[index]"
              class="position-absolute end-0 top-50 me-5 translate-middle-y"
            >
              <div class="spinner-border spinner-border-sm text-secondary" role="status">
                <span class="visually-hidden">Loading...</span>
              </div>
            </div>

            <div v-if="invalid && !entry.school_name_id" class="invalid-feedback d-block">
              Please select your school from the list of suggestions.
            </div>
          </div>

          <!-- Degree -->
          <div class="col-md-6">
            <label :for="'degree_' + index" class="form-label">Degree</label>
            <select
              :id="'degree_' + index"
              v-model="entry.degree_id"
              :class="{ 'is-invalid': invalid && !entry.degree_id }"
              :required="required"
              class="form-select"
            >
              <option disabled value="">Select Degree...</option>
              <option v-for="d in degrees" :key="d.id" :value="String(d.id)">
                {{ d.text || d.name }}
              </option>
            </select>
          </div>

          <!-- Discipline -->
          <div class="col-md-6">
            <label :for="'discipline_' + index" class="form-label">Discipline</label>
            <select
              :id="'discipline_' + index"
              v-model="entry.discipline_id"
              :class="{ 'is-invalid': invalid && !entry.discipline_id }"
              :required="required"
              class="form-select"
            >
              <option disabled value="">Select Discipline...</option>
              <option v-for="di in disciplines" :key="di.id" :value="String(di.id)">
                {{ di.text || di.name }}
              </option>
            </select>
          </div>

          <!-- Start Date -->
          <div class="col-md-6">
            <label class="form-label">Start Date</label>
            <div class="input-group">
              <select
                v-model="entry.start_date.month"
                :class="{ 'is-invalid': invalid && !entry.start_date.month }"
                :required="required"
                class="form-select"
              >
                <option disabled value="">Month</option>
                <option v-for="m in 12" :key="m" :value="String(m)">
                  {{ months[m - 1] }}
                </option>
              </select>
              <input
                v-model="entry.start_date.year"
                :class="{ 'is-invalid': invalid && !entry.start_date.year }"
                :required="required"
                class="form-control"
                placeholder="Year (YYYY)"
                type="number"
              />
            </div>
          </div>

          <!-- End Date -->
          <div class="col-md-6">
            <label class="form-label">End Date</label>
            <div class="input-group">
              <select
                v-model="entry.end_date.month"
                :class="{ 'is-invalid': invalid && !entry.end_date.month }"
                :required="required"
                class="form-select"
              >
                <option disabled value="">Month</option>
                <option v-for="m in 12" :key="m" :value="String(m)">
                  {{ months[m - 1] }}
                </option>
              </select>
              <input
                v-model="entry.end_date.year"
                :class="{ 'is-invalid': invalid && !entry.end_date.year }"
                :required="required"
                class="form-control"
                placeholder="Year (YYYY)"
                type="number"
              />
            </div>
            <div v-if="invalid" class="invalid-feedback d-block">
              Please complete all required education fields.
            </div>
          </div>
        </div>
      </div>
    </div>

    <button class="btn btn-bordered btn-outlined-black btn-sm mt-2" type="button" @click="addEntry">
      + Add another education
    </button>
  </div>
</template>

<script lang="ts" setup>
import { ref, watch } from 'vue'

const props = defineProps<{
  modelValue: any[]
  schools: any[]
  degrees: any[]
  disciplines: any[]
  invalid?: boolean
  required?: boolean
}>()

const emit = defineEmits<{
  (e: 'update:modelValue', value: any[]): void
}>()

const searchTerms = ref<string[]>([])
const schoolResults = ref<any[][]>([])
const showResults = ref<boolean[]>([])
const isSearching = ref<boolean[]>([])
let debounceTimeout: any = null

const months = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December'
]

// Pre-populate search terms from modelValue
watch(
  () => props.modelValue,
  (val) => {
    if (val) {
      val.forEach((entry, idx) => {
        if (entry.school_name_text && !searchTerms.value[idx]) {
          searchTerms.value[idx] = entry.school_name_text
        }
      })
    }
  },
  { immediate: true }
)

function onSchoolSearch(index: number) {
  const term = searchTerms.value[index]
  if (!term || term.length < 2) {
    schoolResults.value[index] = []
    return
  }

  showResults.value[index] = true
  if (debounceTimeout) clearTimeout(debounceTimeout)

  debounceTimeout = setTimeout(async () => {
    isSearching.value[index] = true
    try {
      const data = await $fetch<any>(
        `/api/greenhouse/education/schools?term=${encodeURIComponent(term)}`
      )
      schoolResults.value[index] = data?.items || []
    } catch (e) {
      console.error('Failed to search schools', e)
    } finally {
      isSearching.value[index] = false
    }
  }, 300)
}

function selectSchool(index: number, school: any) {
  const current = props.modelValue || []
  if (current[index]) {
    current[index].school_name_id = String(school.id)
    current[index].school_name_text = school.text || school.name
    searchTerms.value[index] = current[index].school_name_text
  }
  showResults.value[index] = false
  emit('update:modelValue', [...current])
}

function clearSchool(index: number) {
  const current = props.modelValue || []
  if (current[index]) {
    current[index].school_name_id = ''
    current[index].school_name_text = ''
    searchTerms.value[index] = ''
  }
  emit('update:modelValue', [...current])
}

function onBlur(index: number) {
  // Delay hiding results to allow mousedown on items
  setTimeout(() => {
    showResults.value[index] = false
    // Typed text is kept so the applicant can refine it, but only school_name_id makes the
    // entry valid — Greenhouse rejects free-text school names.
  }, 200)
}

function onInput(index: number) {
  const term = searchTerms.value[index]
  const current = props.modelValue || []
  if (current[index]) {
    // Sync text to model immediately
    current[index].school_name_text = term
    // Reset ID if user starts typing a new name
    if (current[index].school_name_id) {
      current[index].school_name_id = ''
    }
  }
  onSchoolSearch(index)
}

function createEmptyEntry() {
  return {
    school_name_id: '',
    school_name_text: '',
    degree_id: '',
    discipline_id: '',
    start_date: { month: '', year: '' },
    end_date: { month: '', year: '' }
  }
}

// Ensure there is at least one entry if empty
if (!props.modelValue || props.modelValue.length === 0) {
  emit('update:modelValue', [createEmptyEntry()])
}

function addEntry() {
  const current = props.modelValue || []
  emit('update:modelValue', [...current, createEmptyEntry()])
}

function removeEntry(index: number) {
  const current = props.modelValue || []
  const newValue = [...current]
  newValue.splice(index, 1)
  emit('update:modelValue', newValue)
}
</script>

<style scoped>
.education-entry {
  border: none;
}

.form-select option {
  color: #212529; /* Standard dark color for Bootstrap or general use */
}
</style>
